const OAUTH_POPUP_FLAG = "oauth_popup";
const OAUTH_PROVIDER_PARAM = "provider";
const OAUTH_COMPLETE_PARAM = "oauth_complete";

type OAuthProvider = "google" | "apple";
type OAuthPopupMessage =
  | { type: "oauth-complete" }
  | { type: "oauth-error"; message: string };

type PopupResult = { handled: boolean; error?: Error };

interface ActiveAttempt {
  cleanup: () => void;
  popup: Window | null;
  resolve: (result: PopupResult) => void;
}

// Module-scope single-flight tracker. Only one popup attempt is allowed at a
// time — starting a new one cancels the prior one (so listener/interval/timeout
// don't stack across provider switches).
let activeAttempt: ActiveAttempt | null = null;

// Set true immediately before triggering a top-level reload after a successful
// popup sign-in. Stale `oauth-complete` messages arriving afterwards (e.g. from
// a prior popup that the user re-opened) are ignored while this is true.
// Failsafe: self-clears after 5s in the unlikely case the reload silently fails,
// so a future successful sign-in can still drive a reload.
let reloadPending = false;

/**
 * Detects if the app is running inside an iframe (e.g. Lovable preview).
 */
const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const getPopupFeatures = () =>
  "popup=yes,width=520,height=720,menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes";

const getPopupEntryUrl = (provider: OAuthProvider): string => {
  const url = new URL("/auth", window.location.origin);
  url.searchParams.set(OAUTH_POPUP_FLAG, "1");
  url.searchParams.set(OAUTH_PROVIDER_PARAM, provider);
  return url.toString();
};

export const isOAuthPopup = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.get(OAUTH_POPUP_FLAG) === "1" && !!window.opener;
};

export const isOAuthPopupCallback = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.get(OAUTH_COMPLETE_PARAM) === "1";
};

export const getOAuthPopupProvider = (): OAuthProvider | null => {
  const provider = new URLSearchParams(window.location.search).get(OAUTH_PROVIDER_PARAM);
  return provider === "google" || provider === "apple" ? provider : null;
};

export const getOAuthPopupRedirectUri = (): string => {
  const url = new URL("/auth", window.location.origin);
  url.searchParams.set(OAUTH_POPUP_FLAG, "1");
  url.searchParams.set(OAUTH_COMPLETE_PARAM, "1");
  return url.toString();
};

export const notifyOAuthOpener = (message: OAuthPopupMessage) => {
  if (!window.opener || window.opener.closed) return;
  window.opener.postMessage(message, window.location.origin);
};

/**
 * Cancels any in-flight popup OAuth attempt: closes the popup window, clears
 * its listener/interval/timeout, and resolves its pending promise as a
 * cancellation (`{ handled: true }`, no error). Safe to call when there is no
 * active attempt.
 *
 * Callers (e.g. provider switch in Auth.tsx) should invoke this BEFORE setting
 * any new loading state, so the prior handler's `finally` runs synchronously
 * with the old loading=false in effect — preventing a microtask race that
 * would clobber the new flow's loading=true.
 */
export const abortPopupOAuth = (): void => {
  const attempt = activeAttempt;
  if (!attempt) return;
  activeAttempt = null;
  try {
    attempt.cleanup();
  } catch {
    // ignore
  }
  try {
    if (attempt.popup && !attempt.popup.closed) {
      attempt.popup.close();
    }
  } catch {
    // ignore
  }
  attempt.resolve({ handled: true });
};

/**
 * Opens OAuth in a popup window when running inside an iframe.
 * The popup itself runs the managed OAuth flow from a top-level window.
 *
 * Single-flight: starting a new attempt cancels any prior in-flight attempt.
 */
export const tryPopupOAuth = async (
  provider: OAuthProvider
): Promise<PopupResult> => {
  if (!isInIframe()) {
    return { handled: false };
  }

  // Cancel any prior in-flight attempt before starting a new one.
  abortPopupOAuth();

  try {
    const popup = window.open(getPopupEntryUrl(provider), "oauth-popup", getPopupFeatures());

    if (!popup) {
      return {
        handled: true,
        error: new Error("Popup blocked. Please allow popups for this site, then try again."),
      };
    }

    popup.focus?.();

    return new Promise<PopupResult>((resolve) => {
      let settled = false;

      const cleanup = () => {
        window.clearTimeout(timeout);
        window.clearInterval(interval);
        window.removeEventListener("message", handleMessage);
      };

      const finish = (result: PopupResult) => {
        if (settled) return;
        settled = true;
        cleanup();
        // Clear active slot if this attempt is still the active one.
        if (activeAttempt && activeAttempt.resolve === resolve) {
          activeAttempt = null;
        }
        resolve(result);
      };

      const handleMessage = (event: MessageEvent<OAuthPopupMessage>) => {
        if (event.origin !== window.location.origin) return;

        if (event.data?.type === "oauth-complete") {
          // Ignore stale completes if a reload is already pending from a prior
          // successful attempt.
          if (reloadPending) {
            finish({ handled: true });
            return;
          }
          try {
            popup.close();
          } catch {
            // ignore
          }
          reloadPending = true;
          // Failsafe: if the reload silently fails, allow future attempts.
          window.setTimeout(() => {
            reloadPending = false;
          }, 5000);
          window.location.reload();
          finish({ handled: true });
        }

        if (event.data?.type === "oauth-error") {
          finish({ handled: true, error: new Error(event.data.message || "Sign-in failed.") });
        }
      };

      const interval = window.setInterval(() => {
        if (popup.closed) {
          finish({ handled: true });
        }
      }, 500);

      const timeout = window.setTimeout(() => {
        finish({
          handled: true,
          error: new Error("Sign-in timed out. Please try again."),
        });
      }, 120000);

      window.addEventListener("message", handleMessage);

      // Register as the active attempt.
      activeAttempt = { cleanup, popup, resolve: finish };
    });
  } catch (err: any) {
    return {
      handled: true,
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
};
